import axios from "axios";
import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import { authStore } from "../auth";
import { env } from "../env";

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export const httpClient = axios.create({
  baseURL: env.apiBaseUrl,
  // Narrower than the axios default, which also accepts text/plain and */*.
  headers: { Accept: "application/json, application/problem+json" },
});

httpClient.interceptors.request.use(async (config) => {
  // Don't even attempt a request offline: getToken() could trigger a refresh
  // that fails and risks logging the user out.
  if (!navigator.onLine) {
    return Promise.reject(new Error("offline"));
  }
  const token = await authStore.getToken();
  config.headers.set("Authorization", `Bearer ${token}`);
  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    if (!config || error.response?.status !== 401 || config._retry) {
      return Promise.reject(error);
    }
    config._retry = true;
    // refresh() is single-flight, so N concurrent 401s share ONE refresh call.
    const token = await authStore.refresh();
    config.headers.set("Authorization", `Bearer ${token}`);
    return httpClient(config);
  },
);
